/* eslint-disable max-lines */
import { action, computed, IArrayWillSplice, intercept, IObservableArray, isArrayLike, observable } from 'mobx';
import { Store } from 'bernie-plugin-mobx';

import { ClientLogger } from 'bernie-client';
import { getErrorString } from 'bernie-logger';
import { ILocalization, Localization } from 'bernie-l10n';
import { merge, SerializedData } from 'bernie-core';

import { STORE_EVENTS } from 'stores/events/store-events';

import { SearchCriteriaUtils } from './search-criteria-utils';

import { RoomModel } from './room-model';

const { withoutKeys } = merge;

const logger = ClientLogger.getLoggerWithIdentifier('pwa.store.searchCriteriaModel');

/**
 * We'll soon phase out the bex-api-lodging definition of search criteria,
 * so we need this separate definition.
 */
export enum PropertySort {
  AARP_MEMBER_DISCOUNT = 'AARP_MEMBER_DISCOUNT',
  BEST_DEAL = 'BEST_DEAL',
  DISTANCE = 'DISTANCE',
  MEMBER_PRICING = 'MEMBER_PRICING',
  PRICE_HIGH_TO_LOW = 'PRICE_HIGH_TO_LOW',
  PRICE_LOW_TO_HIGH = 'PRICE_LOW_TO_HIGH',
  PRICE_RELEVANT = 'PRICE_RELEVANT',
  PROPERTY_CLASS = 'PROPERTY_CLASS',
  PROPERTY_NAME = 'PROPERTY_NAME',
  RECOMMENDED = 'RECOMMENDED',
  REVIEW = 'REVIEW',
  VACATION_RENTAL = 'VACATION_RENTAL',
}

export class SearchCriteriaModel extends Store {
  public static MIDNIGHT_BOOKING_OFFSET_IN_HOURS = 4;
  public static NUM_OF_DAYS_BOOKABLE_IN_ADVANCE = 500;

  public static getDefaults() {
    return {
      destination: '',
      endDate: null,
      group: '',
      latLong: '',
      regionId: '',
      selected: '',
      startDate: null,
      userIntent: '',
      neighborhood: '',
      sort: PropertySort.RECOMMENDED,
    };
  }

  constructor(state: SerializedData = {}, l10n?: ILocalization) {
    super({}, null);

    this.l10n = l10n || new Localization('en_US');
    this.localDateFormat = state.localDateFormat || 'yyyy-MM-dd';

    this.dateInterceptor('startDate');
    this.dateInterceptor('endDate');

    // handle updates like someModel.rooms = []
    intercept(this, 'rooms', change => {
      try {
        if (!change.newValue || !isArrayLike(change.newValue)) {
          change.newValue = [];
        }

        this.rooms.replace(change.newValue);
      } catch (error) {
        const changeMessage = `change.newValue="${typeof change.newValue}"`;
        const errorString = getErrorString(error);
        logger.logEvent(STORE_EVENTS.STORE_INTERCEPT_VALUE_ERROR, errorString, changeMessage);
      }

      return null;
    });

    // handle individual element updates from calls like replace() and push()
    intercept(this.rooms, (change: IArrayWillSplice<RoomModel | any>) => {
      try {
        if (change.added && isArrayLike(change.added)) {
          change.added = change.added.map(item => (item instanceof RoomModel === false ? new RoomModel(item) : item));
        }
      } catch (error) {
        const changeMessage = `change.added="${typeof change.added}"`;
        const errorString = getErrorString(error);
        logger.logEvent(STORE_EVENTS.STORE_INTERCEPT_VALUE_ERROR, errorString, changeMessage);
      }

      return change;
    });

    Object.assign(this, SearchCriteriaModel.acceptedKeys(state));
    this.setDefaultRooms(state);
  }

  // setup mobx interceptor to wrap dates in a method to format data passed to setter
  public dateInterceptor(prop): void {
    if (typeof prop === 'string') {
      intercept(this, prop as any, change => {
        if (change.newValue) {
          const newValue = this.sanitizeDate(change.newValue);

          if (newValue) {
            change.newValue = newValue;

            return change;
          }
        }
        // accept null to clear out dates
        else if (change.newValue === null) {
          return change;
        }
      });
    }
  }

  // keep dates in the data format that we want, in this case ISO, and the UI can localize based on this date
  public sanitizeDate(date) {
    if (date) {
      try {
        date = decodeURIComponent(date);
        let dateObj: Date = this.l10n.parseLocalDateString(date, { raw: this.localDateFormat }) as Date;

        if (!dateObj) {
          dateObj = this.l10n.parseLocalDateString(date, { raw: 'yyyy-MM-dd' }) as Date;
        }

        const cleanDate = this.l10n.formatDate(dateObj, { raw: 'yyyy-MM-dd' });

        if (cleanDate) {
          return cleanDate;
        }

        throw new Error(`Invalid Date: cannot parse ${date} into proper date`);
      } catch (error) {
        if (logger) {
          logger.logEvent(STORE_EVENTS.MOBX_INTERCEPTOR_ERROR, getErrorString(error));
        }

        return '';
      }
    }
  }

  public hydrate(data: SerializedData) {
    if (typeof data === 'object' && data !== null) {
      data = SearchCriteriaModel.acceptedKeys(data);

      Object.assign(this, data);
      this.setDefaultRooms(data);
    }
  }

  // Should not use `null` as a default; may deserialize as empty string.
  public static DEFAULTS: SerializedData = {
    accessibility: [],
    agencyBusinessModel: [],
    amenities: [],
    bedroomFilter: [],
    chain: '',
    commissionTier: [],
    daysInFuture: '',
    deals: [],
    destination: '',
    group: '',
    guestRating: '',
    hotelName: '',
    latLong: '',
    lodging: [],
    mealPlan: [],
    misId: '',
    dropOffTime: '',
    pickUpTime: '',
    airlineCode: '',
    cabinClass: '',
    directFlights: '',
    driverAge: '',
    tripType: '',
    origin: '',
    infantsInSeats: '',
    paymentType: [],
    bedType: [],
    cleaningAndSafetyPractices: [],
    poi: '',
    price: [],
    propertyStyle: [],
    regionId: '',
    neighborhood: '',
    rewards: [],
    roomIndex: '',
    rooms: [],
    selected: '',
    sort: PropertySort.RECOMMENDED,
    star: [],
    stayLength: '',
    theme: '',
    travelerType: [],
    useRewards: '',
    userIntent: '',
    partialStay: 'false',
    mapBounds: [],
    stayType: [],
    expediaPropertyId: '',
  };

  @observable
  public l10n: ILocalization;

  @observable
  public accessibility;
  @observable
  public adultsString: string;
  @observable
  public amenities;
  @observable
  public chain?: string;
  @observable
  public childrenString: string;
  @observable
  public daysInFuture?: string;
  @observable
  public origin: string;
  @observable
  public destination: string;
  @observable
  public endDate: string;
  @observable
  public group: string;
  @observable
  public guestRating: string;
  @observable
  public hotelName: string;
  @observable
  public latLong?: string;
  @observable
  public localDateFormat: string;
  @observable
  public lodging;
  @observable
  public paymentType;
  @observable
  public bedType?: string[];
  @observable
  public cleaningAndSafetyPractices;
  @observable
  public poi: string;
  @observable
  public price: string[];
  @observable
  public regionId: string;
  @observable
  public neighborhood: string;
  @observable
  public roomIndex;
  @observable
  public rooms: IObservableArray<RoomModel>;
  @observable
  public selected;
  @observable
  public semdtl;
  @observable
  public sort;
  @observable
  public star: string[];
  @observable
  public startDate: string;
  @observable
  public stayLength?: string;
  @observable
  public theme: string;
  @observable
  public travelerType;
  @observable
  public useRewards: string;
  @observable
  public userIntent: string;
  @observable
  public bedroomFilter: string[];
  @observable
  public deals;
  @observable
  public propertyStyle;
  @observable
  public multiProp: string;
  @observable
  public misId: string;
  @observable
  public referrerUrl: string;
  @observable
  public rewards: string[];
  @observable
  public pickUpTime: string;
  @observable
  public dropOffTime: string;
  @observable
  public commissionTier;
  @observable
  public agencyBusinessModel;
  @observable
  public mealPlan;
  @observable
  public cabinClass: string;
  @observable
  public tripType: string;
  @observable
  public airlineCode: string;
  @observable
  public directFlights: string;
  @observable
  public infantsInSeats: string;
  @observable
  public driverAge: string;
  @observable
  public paandi: string;
  @observable
  public partialStay: string;
  @observable
  public partialStayStartDate: string;
  @observable
  public partialStayEndDate: string;
  @observable
  public packageRates: string;
  @observable
  public mapBounds: any;
  @observable
  public stayType;
  @observable
  public expediaPropertyId;
  @observable
  public typeaheadRegionType?: number;

  @computed
  public get isPartialStay(): boolean {
    return this.partialStay === 'true';
  }

  @computed
  public get adults(): number {
    if (this.rooms && this.rooms.length > 0) {
      return this.rooms.reduce((base, room) => base + (room.adults || 0), 0);
    }

    return 0;
  }

  @computed
  public get children(): number {
    if (this.rooms && this.rooms.length > 0) {
      return this.rooms.reduce((base, room) => base + ((room.children && room.children.length) || 0), 0);
    }

    return 0;
  }

  @computed
  get guestCount(): number {
    return this.adults + this.children;
  }

  @computed
  get isDateless(): boolean {
    return !this.startDate && !this.endDate;
  }

  public clone(): SearchCriteriaModel {
    return new SearchCriteriaModel(this.toJSON(), this.l10n);
  }

  private setDefaultRooms(state: any = {}) {
    if (!state.rooms || (state.rooms.length === 0 && this.rooms.length === 0)) {
      this.rooms.push(new RoomModel({ children: [], adults: 2 }));
    }
  }

  private static acceptedKeys(data: any = {}): any {
    return withoutKeys(['adults', 'children', 'dateless']).merge({}, data);
  }

  public toJSON(ignoreAdditionalKeys?: string[] | string): SerializedData {
    if (!Array.isArray(ignoreAdditionalKeys)) {
      ignoreAdditionalKeys = ignoreAdditionalKeys && ignoreAdditionalKeys.constructor === String ? [ignoreAdditionalKeys] : [];
    }
    const json = super.toJSON(ignoreAdditionalKeys.concat('rooms', 'l10n'));
    json.rooms = (this.rooms && this.rooms.map(room => room.toJSON())) || [];

    return json;
  }

  @action
  public setValues(query: SerializedData = {}): void {
    if (!query.adults) {
      query.adults = '2';
    }

    if (!query.children || query.children === '0' || query.children === 0) {
      query.children = '';
    }

    query = SearchCriteriaUtils.ensureCorrectTypes(query);

    query.adultsString = query.adults;
    query.childrenString = query.children;

    delete query.adults;
    delete query.children;

    Object.assign(this, query);
  }
}
